import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./StudentSearchBarMulti.module.sass";
import TextInput from "../../../components/TextInput";
import LetterAvatar from "../../../components/LetterAvatar";
import Icon from "../../../components/Icon";
import { searchStudents, getStudentById } from "../../../services/student.service";

const StudentSearchBarMultiple = ({ 
  className,
  label = "Rechercher des étudiants",
  placeholder = "Nom, email ou téléphone (min. 2 caractères)",
  onSelectionChange,
  value = [], // Array d'IDs
  showDetails = false,
  required = false,
  onSubmit,
  loading = false,
  isFormValid = () => true,
  submitButtonText = "Créer le groupe"
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]); // Array d'objets étudiants complets
  const [showResults, setShowResults] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Effectuer la recherche lorsque la requête change
  useEffect(() => {
    const fetchStudents = async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchStudents(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche des étudiants:', error);
          setSearchResults([]);
          setShowResults(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    
    fetchStudents();
  }, [searchQuery]);

  // Charger les objets étudiants à partir des IDs reçus dans value
  useEffect(() => {
    const loadStudentsFromIds = async () => {
      if (value && Array.isArray(value) && value.length > 0) {
        setLoadingStudents(true);
        try {
          // Récupérer les données complètes de chaque étudiant
          const studentsPromises = value.map(studentId => getStudentById(studentId));
          const studentsData = await Promise.all(studentsPromises);
          
          // Filtrer les étudiants valides
          const validStudents = studentsData.filter(student => student && student.id);
          
          // Mettre à jour la liste des étudiants sélectionnés
          setSelectedStudents(validStudents);
        } catch (error) {
          console.error('Erreur lors du chargement des étudiants:', error);
          setSelectedStudents([]);
        } finally {
          setLoadingStudents(false);
        }
      } else {
        setSelectedStudents([]);
        setLoadingStudents(false);
      }
    };

    loadStudentsFromIds();
  }, [value]);

  // Gérer la sélection/désélection d'un étudiant
  const handleSelectStudent = (student) => {
    const isAlreadySelected = selectedStudents.some(s => s.id === student.id);
    let newSelection;
    
    if (isAlreadySelected) {
      // Désélectionner l'étudiant
      newSelection = selectedStudents.filter(s => s.id !== student.id);
    } else {
      // Sélectionner l'étudiant - ajouter l'objet complet
      newSelection = [...selectedStudents, student];
    }
    
    setSelectedStudents(newSelection);
    setSearchQuery(''); // Vider la recherche après sélection
    setShowResults(false);
    
    // Transmettre seulement les IDs au composant parent
    if (onSelectionChange) {
      onSelectionChange({
        target: {
          value: newSelection.map(s => s.id)
        }
      });
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Supprimer un étudiant sélectionné
  const handleRemoveStudent = (studentToRemove) => {
    const newSelection = selectedStudents.filter(s => s.id !== studentToRemove.id);
    setSelectedStudents(newSelection);
    
    if (onSelectionChange) {
      onSelectionChange({
        target: {
          value: newSelection.map(s => s.id)
        }
      });
    }
  };

  // Réinitialiser la recherche
  const handleClearSearch = () => {
    setSearchQuery('');
    setShowResults(false);
  };


  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <TextInput
            className={styles.field}
            label={label}
            name="student-search"
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            required={required}
          />
          {searchQuery && (
            <button className={styles.clearButton} onClick={handleClearSearch} type="button" aria-label="Effacer la recherche">
              <Icon name="close-circle" size="20" />
            </button>
          )}
        </div>
        
        {showResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            {searchResults.map(student => {
              const isSelected = selectedStudents.some(s => s.id === student.id);
              return (
                <div 
                  key={student.id} 
                  className={cn(styles.resultItem, { [styles.selected]: isSelected })}
                  onClick={() => handleSelectStudent(student)}
                >
                  <div className={styles.resultContent}>
                    <div style={{ marginRight: '20px' }}>
                      <LetterAvatar
                        firstName={student.first_name}
                        lastName={student.last_name}
                        size={40}
                      />
                    </div>
                    <div className={styles.studentInfo}>
                      <div className={styles.studentName}>
                        {student.first_name} {student.last_name}
                        {isSelected && (
                          <Icon name="check" size="16" className={styles.checkIcon} />
                        )}
                      </div>
                      <div className={styles.studentDetails}>
                        <span>{student.email}</span>
                        <span>•</span>
                        <span>{student.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && (
          <div className={styles.noResults}>
            Aucun étudiant trouvé
          </div>
        )}
      </div>


      {/* Affichage des étudiants sélectionnés */}
      {(loadingStudents || selectedStudents.length > 0 || (value && value.length > 0)) && (
        <div className={styles.selectedStudents}>
          <div className={styles.selectedHeader}>
            {loadingStudents ? (
              <span className={styles.selectedCount}>Chargement des étudiants...</span>
            ) : (
              <span className={styles.selectedCount}>
                {selectedStudents.length} étudiant{selectedStudents.length > 1 ? 's' : ''} sélectionné{selectedStudents.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {loadingStudents ? (
            <div className={styles.loadingSpinner}>
              <Icon name="loader" size="24" />
              <span>Chargement des étudiants...</span>
            </div>
          ) : (
            <div className={styles.selectedList}>
              {selectedStudents.length > 0 ? (
                [...selectedStudents]
                  .sort((a, b) => {
                    const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                    const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
                    return nameA.localeCompare(nameB, 'fr');
                  })
                  .map(student => (
                  <div key={student.id} className={styles.selectedItem}>
                    <div className={styles.selectedContent}>
                      <LetterAvatar
                        firstName={student.first_name || ''}
                        lastName={student.last_name || ''}
                        size={32}
                      />
                      <span className={styles.selectedName}>
                        {student.first_name || ''} {student.last_name || ''}
                      </span>
                    </div>
                    <button 
                      className={styles.removeButton}
                      onClick={() => handleRemoveStudent(student)}
                      type="button"
                      aria-label={`Retirer ${student.first_name || ''} ${student.last_name || ''}`}
                    >
                      <Icon name="close" size="16" />
                    </button>
                  </div>
                ))
              ) : (
                <div className={styles.noStudentsSelected}>
                  Aucun étudiant trouvé pour ce groupe
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bouton de sauvegarde */}
      {onSubmit && (
        <div className={styles.submitSection}>
          <button 
            className={cn("button", styles.submitButton, { [styles.loading]: loading })}
            type="button"
            onClick={onSubmit}
            disabled={loading || !isFormValid()}
          >
            {loading ? "Création en cours..." : submitButtonText}
          </button>
        </div>
      )}

    </div>
  );
};

export default StudentSearchBarMultiple;
